'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';

// Define a minimal interface for our analytics needs
interface AnalyticsAPI {
  page: (name?: string, properties?: Record<string, any>) => void;
  identify: (userId: string, traits?: Record<string, any>) => void;
  track: (event: string, properties?: Record<string, any>) => void;
  reset: () => void;
  // Add other methods you use
}

// Null implementation for SSR and initial client render
const nullAnalytics: AnalyticsAPI = {
  page: () => {},
  identify: () => {},
  track: () => {},
  reset: () => {}
};

// Create context with null implementation as default
const CustomerIOContext = createContext<AnalyticsAPI>(nullAnalytics);

type CustomerIOProviderAppRouterProps = {
  writeKey: string;
  siteId?: string;
  options?: any;
  children: ReactNode;
};

export function CustomerIOProviderAppRouter({ writeKey, siteId, options, children }: CustomerIOProviderAppRouterProps) {
  // Start with null implementation
  const [analytics, setAnalytics] = useState<AnalyticsAPI>(nullAnalytics);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load the actual SDK only in the browser
  useEffect(() => {
    let isMounted = true;

    const initAnalytics = async () => {
      try {
        // Dynamically import the Customer.io SDK
        const { AnalyticsBrowser } = await import('@customerio/cdp-analytics-browser');

        const initOptions = {
          ...options,
          initialPageview: false
        };

        if (siteId) {
          initOptions.integrations = {
            'Customer.io In-App Plugin': { siteId }
          };
        }

        // Only update state if component is still mounted
        if (isMounted) {
          const instance = AnalyticsBrowser.load({ writeKey }, initOptions);
          setAnalytics(instance);
          setIsInitialized(true);
          console.log('[Customer.io] Successfully initialized');
        }
      } catch (error) {
        console.error('[Customer.io] Failed to initialize:', error);
      }
    };

    initAnalytics();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [writeKey, siteId, options]);

  // Handle user identification
  const { data: session, status } = useSession();
  const lastIdentifiedUserId = useState<string | null>(null);

  useEffect(() => {
    if (!isInitialized || status !== 'authenticated' || !session?.user) {
      return;
    }

    const { id, email, name, subscriptionPlan } = session.user;

    if (lastIdentifiedUserId[0] !== id) {
      console.log('[Customer.io] Identifying user:', id);
      analytics.identify(id, {
        email,
        name,
        subscriptionPlan: subscriptionPlan || 'free'
      });
      lastIdentifiedUserId[1](id);
    }
  }, [analytics, isInitialized, session, status, lastIdentifiedUserId]);

  // Track page views using App Router navigation
  const pathname = usePathname();

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    // Track page view on pathname change
    analytics.page();
  }, [analytics, isInitialized, pathname]);

  return <CustomerIOContext.Provider value={analytics}>{children}</CustomerIOContext.Provider>;
}

export function useCustomerIoAnalytics(): AnalyticsAPI {
  return useContext(CustomerIOContext);
}
